import { useCallback, useRef } from "react";
import { checkAim, getMousePosition, Point } from "../utils/flyoutMenuUtils";

interface UseFlyoutMenuParams {
  setHighlightedRowIdx: React.Dispatch<React.SetStateAction<number | null>>;
  highlightedRowIdx: number | null;
  flyoutEl: Element | null;
}

export const useFlyoutMenu = ({
  setHighlightedRowIdx,
  highlightedRowIdx,
  flyoutEl,
}: UseFlyoutMenuParams) => {
  const mouseHistory = useRef<Array<Point>>([]);

  const updateMouseHistory = useCallback((position: Point) => {
    mouseHistory.current.push(position);
    if (mouseHistory.current.length > 2) {
      mouseHistory.current.shift();
    }
  }, []);

  const isMouseMovingToFlyout = useCallback(
    (currentPosition: Point) => {
      if (mouseHistory.current.length < 2) {
        return false;
      }
      const prevPosition = mouseHistory.current[0];
      return checkAim(flyoutEl, currentPosition, prevPosition);
    },
    [flyoutEl]
  );

  const scheduleHighlight = useCallback(
    (rowIndex: number) => {
      const latestMousePosition = mouseHistory.current[mouseHistory.current.length - 1];
      if (!isMouseMovingToFlyout(latestMousePosition)) {
        setHighlightedRowIdx(rowIndex);
      }
    },
    [isMouseMovingToFlyout, setHighlightedRowIdx]
  );

  const handleMouseMove = useCallback(
    (rowIndex: number, event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (highlightedRowIdx === null) {
        setHighlightedRowIdx(rowIndex);
        return;
      }
      const currentPosition = getMousePosition(event.nativeEvent);
      updateMouseHistory(currentPosition);

      const currentIsMouseWithinSafeZone = isMouseMovingToFlyout(currentPosition);
      if (!currentIsMouseWithinSafeZone) {
        scheduleHighlight(rowIndex);
      }
    },
    [
      updateMouseHistory,
      isMouseMovingToFlyout,
      scheduleHighlight,
      highlightedRowIdx,
      setHighlightedRowIdx,
    ]
  );

  const handleContainerMouseLeave = useCallback(() => {
    setHighlightedRowIdx(null);
  }, [setHighlightedRowIdx]);

  return {
    handleMouseMove,
    handleContainerMouseLeave,
  };
};
